import { useNavigation, useRoute } from "@react-navigation/native";
import type { RouteProp } from "@react-navigation/native";
import type { StackNavigationProp } from "@react-navigation/stack";
import React from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
} from "react-native";

import type { RootStackParamList } from "../../navigation/types";
import PremiumBackgroundUniversal from "../../shared/ui/PremiumBackgroundUniversal";

interface ResultsScreenProps {
  totalQuestions: number;
  correctAnswers: number;
  timeTaken?: number; // in seconds
  onPlayAgain: () => void;
  onGoHome: () => void;
  onReview: () => void;
}

const ResultsScreen: React.FC<ResultsScreenProps> = ({
  totalQuestions,
  correctAnswers,
  timeTaken,
  onPlayAgain,
  onGoHome,
  onReview,
}) => {
  const percent = Math.round((correctAnswers / totalQuestions) * 100);
  let feedback = "Great job!";
  if (percent === 100) feedback = "Perfect! 🎉";
  else if (percent >= 80) feedback = "Excellent!";
  else if (percent >= 60) feedback = "Good effort!";
  else feedback = "Keep practicing!";

  return (
    <View style={{ flex: 1, backgroundColor: "#fff" }} data-oid="jsl97pl">
      <PremiumBackgroundUniversal confetti={false} />
      <ScrollView contentContainerStyle={styles.container} data-oid="u:aod4-">
        <Text style={styles.title} data-oid=":ac3p_l">
          Quiz Results
        </Text>
        <Text style={styles.score} data-oid="6av0a_9">
          {correctAnswers} / {totalQuestions}
        </Text>
        <Text style={styles.percent} data-oid="v0g4cnh">
          {percent}% correct
        </Text>
        <Text style={styles.feedback} data-oid="avt:2zp">
          {feedback}
        </Text>
        {typeof timeTaken === "number" && (
          <Text style={styles.time} data-oid="7l6bh1y">
            Time: {Math.floor(timeTaken / 60)}m {timeTaken % 60}s
          </Text>
        )}
        <View style={styles.buttonRow} data-oid="1a6_7ja">
          <TouchableOpacity
            style={[styles.button, styles.primary]}
            onPress={onPlayAgain}
            data-oid="ld9_ds-"
          >
            <Text style={styles.buttonText} data-oid="dvl3jj7">
              Play Again
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.button}
            onPress={onReview}
            data-oid="npmqdqm"
          >
            <Text
              style={[styles.buttonText, { color: "#38bdf8" }]}
              data-oid="6vgi_-z"
            >
              Review Answers
            </Text>
          </TouchableOpacity>
        </View>
        <TouchableOpacity
          style={styles.secondaryButton}
          onPress={onGoHome}
          data-oid="vg00z3h"
        >
          <Text
            style={[styles.buttonText, { color: "#0284c7" }]}
            data-oid="6lsxlbw"
          >
            Go Home
          </Text>
        </TouchableOpacity>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    paddingBottom: 48,
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
    color: "#0ea5e9",
    marginBottom: 12,
    marginTop: 24,
  },
  score: {
    fontSize: 48,
    fontWeight: "bold",
    color: "#38bdf8",
    marginBottom: 8,
  },
  percent: {
    fontSize: 20,
    color: "#0369a1",
    marginBottom: 6,
  },
  feedback: {
    fontSize: 20,
    fontWeight: "600",
    color: "#111827",
    marginBottom: 18,
  },
  time: {
    fontSize: 16,
    color: "#64748b",
    marginBottom: 18,
  },
  buttonRow: {
    flexDirection: "row",
    justifyContent: "center",
    marginBottom: 18,
  },
  button: {
    backgroundColor: "#e0f2fe",
    borderRadius: 16,
    paddingVertical: 14,
    paddingHorizontal: 24,
    marginHorizontal: 8,
    alignItems: "center",
    minWidth: 120,
  },
  primary: {
    backgroundColor: "#38bdf8",
  },
  buttonText: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#fff",
  },
  secondaryButton: {
    borderRadius: 16,
    borderWidth: 2,
    borderColor: "#38bdf8",
    paddingVertical: 12,
    paddingHorizontal: 18,
    alignItems: "center",
    marginTop: 8,
    backgroundColor: "#f0f9ff",
  },
});

export const ResultsScreenWithNav: React.FC = () => {
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, "Results">>();
  // Ensure we always pass a number or undefined for timeTaken
  const { score, totalQuestions, timeTaken, categoryId, difficulty_id } =
    route.params || {};
  const safeTimeTaken = typeof timeTaken === "number" ? timeTaken : undefined;

  // Only pass valid QuizGame params
  const quizGameParams = {
    category: "", // fallback empty, ideally should be passed through navigation
    categoryId: categoryId ?? 0,
    difficulty_id: difficulty_id ?? 0,
  };

  // For review, pass reviewAnswers: true and reviewData (questions, answers)
  const handleReview = () => {
    if (route.params && route.params.reviewData) {
      navigation.navigate("SinglePlayerGame", {
        category: quizGameParams.category,
        categoryId: quizGameParams.categoryId,
        difficulty_id: quizGameParams.difficulty_id,
        reviewAnswers: true,
        reviewData: route.params.reviewData,
      } as any);
    }
  };

  return (
    <ResultsScreen
      totalQuestions={totalQuestions ?? 0}
      correctAnswers={score ?? 0}
      timeTaken={safeTimeTaken}
      onPlayAgain={() =>
        navigation.replace("SinglePlayerGame", {
          ...quizGameParams,
          questionCount: totalQuestions ?? 10,
        })
      } // Ensure questionCount is provided
      onGoHome={() => navigation.popToTop()}
      onReview={handleReview}
      data-oid="wu4r280"
    />
  );
};

export default ResultsScreenWithNav;
